import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 outline-none",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 " +
          "focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 " +
          "focus-visible:ring-2 focus-visible:ring-destructive/40",
        outline:
          "border bg-background text-foreground hover:bg-accent hover:text-accent-foreground " +
          "focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 " +
          "focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2",
        ghost:
          "hover:bg-accent hover:text-accent-foreground " +
          "focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3",
        lg: "h-10 rounded-md px-6",
        icon: "size-9 rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
